import { useTranslation } from "react-i18next";

import { Account } from "services/account/account.types";
import { MyTenant } from "services/costs/types";
import { Region } from "services/organization";

import ContextMenuItem from "components/ContextMenu/ContextMenuItem";
import FormRow from "components/FormRow";
import formRowStyles from "components/FormRow/FormRow.module.scss";
import ListView from "components/ListView/ListView";
import { Modal } from "components/Modal/Modal";
import OutlinedSelect from "components/OutlinedSelect";
import { TextInput } from "components/TextInput/TextInput";

import { useAccountActions } from "../../hooks/useAccountActions";
import { InfoSection } from "./InfoSection";

import styles from "./AccountFormModal.module.scss";

interface Props {
  availableRegions: Region[];
  onClose: () => void;
  onCreate?: (account: { accountName: string }) => void;

  /*
   * If account is {}, the form will be in "create" mode.
   * Otherwise, it will be in "edit" mode.
   */
  account: Account | {};
  tenants: MyTenant[];
}

const AccountFormModal = (props: Props) => {
  const { t } = useTranslation();
  const { onClose, onCreate, availableRegions, account, tenants } = props;

  const tenant = tenants.find(
    t => t.account_id === (account as Account).accountId
  );

  const {
    editMode,
    loading,
    createMutation,
    fieldsErrors,
    formFields,
    setFormFields,
    updateMutation,
    validateForm,
    isStateDirty,
    formWasEdited,
    setFormWasEdited,
  } = useAccountActions({
    regions: availableRegions,
    account,
    onClose,
    onCreate,
  });

  const isFormValid =
    !fieldsErrors.name && !fieldsErrors.region && !!formFields.name;

  const renderAccountInfo = () => {
    if (!("accountId" in account)) {
      return null;
    }

    return (
      <ListView
        spacingPx={24}
        direction="vertical"
        className={styles.info}
      >
        <InfoSection
          title={t("accounts.form.account_id")}
          text={account.accountId}
          infoToCopy={account.accountId}
        />

        <InfoSection
          title={t("accounts.form.account_url")}
          text={account.url}
          infoToCopy={account.url}
        />

        {!!tenant?.tenant_id && (
          <InfoSection
            title={t("accounts.form.aws_account_id")}
            text={tenant.tenant_id}
            infoToCopy={tenant.tenant_id}
          />
        )}
      </ListView>
    );
  };

  const handleInputChange = (name: string, value: string) => {
    setFormFields({ ...formFields, [name]: value });
    if (!formWasEdited) {
      setFormWasEdited(true);
    }
  };

  const handleFormSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!isStateDirty() || loading) {
      return;
    }

    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    if (editMode) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  const title = editMode
    ? t("accounts.form.edit_account")
    : t("accounts.form.create_account");

  const primaryButton = editMode
    ? {
        text: "accounts.form.update.submit",
        onSubmit: handleFormSubmit,
        isLoading: loading,
        disabled: !isFormValid || !isStateDirty() || loading,
        dataTestid: "update-account-submit",
      }
    : {
        text: "accounts.form.create.submit",
        onSubmit: handleFormSubmit,
        isLoading: loading,
        disabled: !isFormValid || loading,
        dataTestid: "create-account-submit",
      };

  return (
    <Modal
      title={title}
      onClose={onClose}
      primaryButton={primaryButton}
      maxWidth="sm"
    >
      <div className={styles.root}>
        <FormRow
          required
          label={t("accounts.form.account_name")}
          input={
            <TextInput
              testId="account-name-field"
              fullWidth
              onChange={e => handleInputChange("name", e.target.value)}
              error={!!fieldsErrors.name}
              helperText={fieldsErrors.name}
              autoFocus
              disabled={loading}
              value={formFields.name}
              inputRootClassName={formRowStyles.nameInput}
              className={formRowStyles.nameInputContainer}
            />
          }
        />

        <FormRow
          label={t("accounts.form.region")}
          input={
            <OutlinedSelect
              searchOptions={{
                searchPlaceholder: t("accounts.form.region"),
                noResultsText: t("configuration.no_results") as string,
              }}
              allowUncheck={false}
              testId="region-select"
              controlledValue={formFields.region ? [formFields.region] : []}
              disabled={editMode || loading}
              onSelect={(items: string[]) => {
                if (!items.length) return;
                const [value] = items;
                handleInputChange("region", value);
              }}
            >
              {availableRegions.map(region => (
                <ContextMenuItem
                  key={region.name}
                  value={region.name}
                  testId={`region-${region.name}`}
                  text={region.displayName}
                  checked={formFields.region === region.name}
                />
              ))}
            </OutlinedSelect>
          }
        />

        {editMode && renderAccountInfo()}
      </div>
    </Modal>
  );
};

export default AccountFormModal;
